/**
 * Header Class
 */
export class Header {

	/**
	 * Header element.
	 * @type {HTMLElement|null}
	 */
	#header;

	/**
	 * Navigation element.
	 * @type {HTMLElement|null}
	 */
	#navigation;

	/**
	 * Navigation menu <ul> element.
	 * @type {HTMLElement|null}
	 */
	#navMenu;

	/**
	 * Opened dropdown element.
	 * @type {HTMLElement|null}
	 */
	#openedDropdown;

	/**
	 * Hamburger menu button.
	 * @type {HTMLElement|null}
	 */
	#hamburgerMenu;

	/**
	 * Language switcher menu button.
	 * @type {HTMLElement|null}
	 */
	#languageSwitcher;

	/**
	 * Language switcher parent.
	 * @type {HTMLElement|null}
	 */
	#languageSwitcherOriginalParent;

	/**
	 * Language switcher parent sibling element.
	 * @type {HTMLElement|null}
	 */
	#languageSwitcherNextSibling;

	/**
	 * Megamenu searchbar element.
	 * @type {HTMLElement|null}
	 */
	#megamenuSearchbar;

	/**
	 * NavExtras searchbar element.
	 * @type {HTMLElement|null}
	 */
	#navExtras;

	/**
	 * Main navigation element (.b-main-nav).
	 * @type {HTMLElement|null}
	 */
	#mainNav;


	/**
	 * Checks if device is in mobile mode.
	 * @type {boolean}
	 */
	#isMobile = false;

	/**
	 * Stores a reference to the root HTML element.
	 * @type {HTMLElement|null}
	 */
	#root;

	/**
	 * Stores a reference to the body HTML element.
	 * @type {HTMLElement|null}
	 */
	#body;

	/**
	 * Stores the debounced version of `setMainStyles` to prevent excessive calls during resize events.
	 *
	 * @type {Function|undefined}
	 */
	#debounceSetMainStyles;

	/**
	 * Variable to store the original padding top value.
	 * @type {null}
	 */
	#originalPaddingTop = null;

	/**
	 * Represents the vertical scroll position.
	 * @type {number}
	 */
	#scrollY = 0;

	/**
	 * Creates an instance of a class.
	 *
	 * @return {void} Returns nothing.
	 */
	constructor() {

		const mobileBreakpoint = getComputedStyle( document.documentElement )
			.getPropertyValue( '--mobile-breakpoint' )
			.trim();
		this.#isMobile = window.matchMedia(
			`(max-width: ${mobileBreakpoint})`
		).matches;
		this.#header = document.querySelector( '.b-header' );
		this.#hamburgerMenu = this.#header?.querySelector( '.c-hamburger' ) || null;
		this.#languageSwitcher = this.#header?.querySelector( '.b-language-switcher' ) || null;
		this.#languageSwitcherOriginalParent = this.#languageSwitcher?.parentElement || null;
		this.#languageSwitcherNextSibling = this.#languageSwitcher?.nextElementSibling || null;

		this.#megamenuSearchbar = this.#header?.querySelector( '.b-megamenu-search' ) || null;
		this.#navExtras = this.#header?.querySelector( '.b-nav-extras' ) || null;

		this.#mainNav = this.#header?.querySelector( '.b-main-nav' ) || null;
		this.#navigation = this.#header?.querySelector( '.b-nav' ) || null;
		this.#navMenu = this.#navigation?.querySelector( '.b-nav__menu' ) || null;
		this.#openedDropdown = this.#navigation?.querySelector( '.b-nav-dropdown--open' ) || null;

		this.#root = document.querySelector( ':root' );
		this.#body = document.querySelector( 'body' );

		if ( document.readyState === 'loading' ) {

			document.addEventListener( 'load', () => this.init() );

		} else {

			setTimeout( () => this.init(), 100 );

		}

	}

	/**
	 * Initializes header functionality.
	 *
	 * @return {void} Returns nothing.
	 */
	init = () => {

		requestAnimationFrame( () => {

			// this.setFirstBlockHeight();
			this.setMainStyles();
			this.handleScrollClass();

		} );
		this.toggleMobileMenu();
		this.toggleDropdownMenu();
		this.toggleMobileSubmenu();
		this.moveLanguageSwitcher();
		this.moveMegamenuSearchbar();
		document.addEventListener( 'click', this.handleClickOutside );
		window.addEventListener( 'scroll', this.handleScrollClass );

	};

	/**
	 * Handles click events outside of a specific navigation element.
	 * @param {Event} event - The click event that triggered the handleClickOutside function.
	 */
	handleClickOutside = ( event ) => {

		if ( this.#navigation && this.#navigation.contains( event.target ) ) {

			return;

		}

		if ( this.#navigation ) {

			this.closeAllDropdowns();

		}

	};

	/**
	 * Handles the resize event and applies debounced updates to the main styles.
	 * This function is added as a callback to resizeHandler function.
	 *
	 * @param {boolean} isMobile - Indicates whether the current view is mobile.
	 * @return {void}
	 */
	onResize = ( isMobile ) => {

		this.#isMobile = isMobile;

		if ( !this.#debounceSetMainStyles ) {

			this.#debounceSetMainStyles = this.debounce( () => {

				this.setMainStyles();

			}, 200 );

		}
		this.#debounceSetMainStyles();
		this.moveLanguageSwitcher();
		this.moveMegamenuSearchbar();

		if ( !this.#isMobile ) {

			this.#body.classList.remove( "no-scroll", "menu-mobile-is-open" );
			document.documentElement.classList.remove( "no-scroll" );

			this.#body.style.position = "";
			this.#body.style.top = "";
			this.#body.style.width = "";
			this.#body.style.maxHeight = "";
			this.#body.style.overflowY = "";

			this.#navigation?.classList.remove( "b-nav--open" );
			this.#hamburgerMenu?.classList.remove( "c-hamburger--open" );

		}

	};

	/**
	 * Creates a debounced function that delays the execution of `func` until after `wait` milliseconds have elapsed.
	 *
	 * @param {Function} func - The function to debounce.
	 * @param {number} wait - The number of milliseconds to delay.
	 * @return {Function} - A debounced function.
	 */
	debounce = ( func, wait ) => {

		let timeout;
		return function debouncedFunction( ...args ) {

			clearTimeout( timeout );
			timeout = setTimeout( () => func.apply( this, args ), wait );

		};

	};

	/**
	 * Function to add or remove a CSS class based on the scroll position of the window.
	 * If the scroll position is greater than or equal to 150, it adds the CSS class 'scrolled' to the body element.
	 * If the scroll position is less than 200, it removes the CSS class 'scrolled' from the body element.
	 */
	handleScrollClass = () => {

		if ( window.scrollY >= 30 ) {

			this.#body.classList.add( 'scrolled' );

		} else {

			this.#body.classList.remove( 'scrolled' );

		}

	};

	/**
	 * Sets header styles.
	 *
	 * @return {void} Returns nothing.
	 */
	setMainStyles = () => {

		const headerHeight = this.#header?.offsetHeight;

		if ( !headerHeight || !this.#root ) {

			return;

		}

		if ( this.#navigation ) {

			const openedDropdown = this.#navigation.querySelector(
				'.b-nav-dropdown--open'
			);
			if ( openedDropdown ) {

				requestAnimationFrame( () => {

					openedDropdown.style.setProperty(
						'--dropdown-height',
						`${openedDropdown.scrollHeight}px`
					);

				} );

			}

			const dropdownColumns = this.#navigation.querySelectorAll(
				'.b-nav-dropdown-column__inner'
			);

			dropdownColumns.forEach( ( column ) => {

				requestAnimationFrame( () => {

					column.style.setProperty(
						'--dropdown-content-height',
						`${column.scrollHeight}px`
					);

				} );

			} );

		}


		requestAnimationFrame( () => {

			this.#root.style.setProperty(
				'--header-height',
				`${headerHeight}px`
			);
			this.#root.style.setProperty(
				'--header-height-single',
				`${headerHeight}px`
			);

		} );



	};

	/**
	 * Toggle mobile menu on click.
	 *
	 * @return {void} Returns nothing.
	 */
	toggleMobileMenu = () => {

		if ( !this.#hamburgerMenu ) {

			return;

		}

		this.#hamburgerMenu.addEventListener( 'click', () => {

			this.updateMobileMenuState();

		} );

	};

	/**
	 * Adds a back button to the dropdown menu.
	 *
	 * @param {HTMLElement} menuItemLink - The link element representing the menu item.
	 * @return {void}
	 */
	addBackButtonToDropdown( menuItemLink ) {

		const buttonTitle = menuItemLink.querySelector(
			'.b-nav-item__link-title'
		);
		const buttonText = buttonTitle
			? buttonTitle.textContent.trim()
			: menuItemLink.textContent.trim();
		const menuItem = menuItemLink.closest( '.b-nav-item' );
		const dropdown = menuItem.querySelector( '.b-nav-dropdown' );

		if ( dropdown ) {

			const existingBackBtn = dropdown.querySelector(
				'.b-nav-dropdown__back-button'
			);
			if ( existingBackBtn ) {

				existingBackBtn.remove();

			}

			const backBtn = document.createElement( 'button' );
			backBtn.className = 'b-nav-dropdown__back-button';
			backBtn.type = 'button';
			backBtn.textContent = buttonText;

			backBtn.addEventListener( 'click', ( e ) => {

				e.stopPropagation();
				this.closeAllDropdowns();

			} );

			dropdown.insertAdjacentElement( 'afterbegin', backBtn );

			const backLabel = window.themeVars?.translations?.back || 'Back';
			backBtn.setAttribute( 'data-label', backLabel );

		}

	}

	/**
	 * Moves the megamenu search bar based on device type.
	 * If the megamenu search bar exists and the device is mobile, it appends the search bar to the main navigation.
	 * If the device is not mobile, it places the search bar back in its original parent, considering next sibling if available.
	 */
	moveMegamenuSearchbar = () => {

		if ( !this.#megamenuSearchbar || !this.#mainNav || !this.#navExtras ) {

			return;

		}

		if ( this.#isMobile ) {

			if ( this.#megamenuSearchbar.parentElement !== this.#mainNav ) {

				this.#mainNav.appendChild( this.#megamenuSearchbar );

			}

		} else if ( this.#megamenuSearchbar.parentElement !== this.#navExtras ) {

			this.#navExtras.appendChild( this.#megamenuSearchbar );

		}

	};

	/**
	 * Sets the height of the first block below the header based on the header height.
	 * If the header element is not found or the first block is not found, the function exits early.
	 * It retrieves the header height from CSS variable '--header-height' of the root element.
	 * Then, it searches for the first block element following the header.
	 * If a block is found, it adjusts the padding top of the block by adding the header height.
	 * If the current padding top includes a CSS variable, it adds the calculated value to it.
	 * If no padding top is set, it sets the padding top to the header height.
	 */
	setFirstBlockHeight = () => {

		if ( !this.#header ) {

			return;

		}
		const mainGroup = document.querySelector(
			'main .wp-block-group:first-of-type'
		);
		if ( !mainGroup ) {

			return;

		}

		const allChildren = mainGroup.querySelectorAll( '*' );
		let firstBlock = null;
		for ( const el of allChildren ) {

			if (
				Array.from( el.classList ).some( ( cls ) =>
					cls.startsWith( 'wp-block-' )
				)
			) {

				firstBlock = el;
				break;

			}

		}
		if ( !firstBlock ) {

			return;

		}

		const headerHeight = this.#header.offsetHeight;
		const headerHeightCss = `${headerHeight}px`;

		while (
			firstBlock &&
			!Array.from( firstBlock.classList ).some( ( cls ) =>
				cls.startsWith( 'wp-block' )
			)
		) {

			firstBlock = firstBlock.nextElementSibling;

		}

		if ( this.#originalPaddingTop === null ) {

			this.#originalPaddingTop = firstBlock.style.paddingTop || '';

		}

		firstBlock.style.paddingTop = this.#originalPaddingTop
			? `calc(${headerHeightCss} + ${this.#originalPaddingTop})`
			: headerHeightCss;

	};

	/**
	 * Moves the language switcher element to a specific location based on the current context
	 */
	moveLanguageSwitcher = () => {

		if ( !this.#languageSwitcher ) {

			return;

		}

		if ( this.#isMobile ) {

			if (
				this.#navMenu &&
				!this.#navMenu.contains( this.#languageSwitcher )
			) {

				this.#navMenu.appendChild( this.#languageSwitcher );

			}

		} else if (
			this.#languageSwitcherOriginalParent &&
			!this.#languageSwitcherOriginalParent.contains(
				this.#languageSwitcher
			)
		) {

			if ( this.#languageSwitcherNextSibling && this.#languageSwitcherNextSibling.parentNode === this.#languageSwitcherOriginalParent ) {

				this.#languageSwitcherOriginalParent.insertBefore(
					this.#languageSwitcher,
					this.#languageSwitcherNextSibling
				);

			} else {

				this.#languageSwitcherOriginalParent.appendChild(
					this.#languageSwitcher
				);

			}

		}

	};

	/**
	 * Handles toggling the mobile menu and resets the top bar menu state.
	 * @return {void}
	 */
	updateMobileMenuState = () => {

		this.#navigation.classList.toggle( 'b-nav--open' );
		this.#hamburgerMenu.classList.toggle( 'c-hamburger--open' );
		this.#body.classList.toggle( 'no-scroll' );
		this.#body.classList.toggle( 'menu-mobile-is-open' );

		const isMenuOpen = this.#body.classList.contains( 'menu-mobile-is-open' );

		if ( isMenuOpen ) {

			this.#scrollY = window.scrollY;
			this.#body.style.position = 'fixed';
			this.#body.style.top = `0px`;
			this.#body.style.width = '100%';
			this.#body.style.maxHeight = '100vh';
			this.#body.style.overflowY = 'hidden';

		} else {

			const html = document.documentElement;

			this.#body.style.position = '';
			this.#body.style.top = '';
			this.#body.style.width = '';
			this.#body.style.maxHeight = '';
			this.#body.style.overflowY = '';
			html.style.scrollBehavior = 'auto';
			window.scrollTo( 0, this.#scrollY );
			html.style.scrollBehavior = 'smooth';

		}

	};

	/**
	 * Loads all images inside the header element by replacing the 'src' attribute with the 'data-src' attribute value.
	 * Removes the 'data-src' attribute after setting the 'src' attribute.
	 */
	loadAllImages = () => {

		this.#header
			.querySelectorAll( '.b-nav-dropdown img[data-src]' )
			.forEach( ( img ) => {

				img.setAttribute( 'src', img.getAttribute( 'data-src' ) );
				img.removeAttribute( 'data-src' );

			} );

	};

	toggleDropdownMenu = () => {

		if ( !this.#navigation ) {

			return;

		}
		this.#navigation.addEventListener( 'click', ( e ) => {

			const menuItemLink = e.target.closest(
				'.b-nav-item__link.has-dropdown'
			);
			if ( menuItemLink ) {

				const isOpen = menuItemLink.classList.contains(
					'b-nav-item__link--open'
				);
				this.closeAllDropdowns();
				this.loadAllImages();
				if ( !isOpen ) {

					this.openDropdown( menuItemLink );
					this.addBackButtonToDropdown( menuItemLink );

				}

			}

		} );

	};

	/**
	 * Closes all dropdown menus in the navigation menu.
	 * Removes 'menu-is-open' class from body element and closes all dropdowns in the navigation menu.
	 * Also resets the classes and attributes of dropdown links and items.
	 *
	 * @return {void}
	 */
	closeAllDropdowns() {

		this.#body.classList.remove( 'menu-is-open' );
		const allLinks = this.#navigation.querySelectorAll(
			'.b-nav-item__link.has-dropdown'
		);
		const allItems = this.#navigation.querySelectorAll( '.b-nav-item' );
		const allDropdowns =
			this.#navigation.querySelectorAll( '.b-nav-dropdown' );
		const backButtons = this.#navigation.querySelectorAll(
			'.b-nav-dropdown__back-button'
		);

		allLinks.forEach( ( link ) => {

			link.classList.remove( 'b-nav-item__link--open' );
			link.setAttribute( 'aria-expanded', 'false' );

		} );
		allItems.forEach( ( item ) => item.classList.remove( 'b-nav-item--open' ) );
		allDropdowns.forEach( ( dropdown ) => {

			dropdown.classList.remove( 'b-nav-dropdown--open' );
			dropdown.style.removeProperty( '--dropdown-height' );

		} );

		setTimeout( () => {

			backButtons.forEach( ( btn ) => btn.remove() );

		}, 300 );

	}

	/**
	 * Opens the dropdown menu related to the specified menu item link element.
	 *
	 * @param {HTMLElement} menuItemLink - The menu item link element that triggers the dropdown opening.
	 *
	 * @return {void}
	 */
	openDropdown( menuItemLink ) {

		this.#body.classList.add( 'menu-is-open' );
		menuItemLink.classList.add( 'b-nav-item__link--open' );
		menuItemLink.setAttribute( 'aria-expanded', 'true' );

		const menuItem = menuItemLink.closest( '.b-nav-item' );
		menuItem.classList.add( 'b-nav-item--open' );

		const dropdown = menuItem.querySelector( '.b-nav-dropdown' );
		if ( dropdown ) {

			dropdown.classList.add( 'b-nav-dropdown--open' );
			requestAnimationFrame( () => {

				const dropdownHeight = dropdown.scrollHeight;
				dropdown.style.setProperty(
					'--dropdown-height',
					`${dropdownHeight}px`
				);

			} );

		}

	}

	/**
	 * Toggles the visibility of the mobile submenu when a menu item is clicked.
	 *
	 * @return {void} Returns nothing.
	 */
	toggleMobileSubmenu = () => {

		if ( !this.#navigation ) {

			return;

		}

		this.#navigation.addEventListener( 'click', ( e ) => {

			if ( !this.#isMobile ) {

				return;

			}

			const openedDropdown = this.#navigation.querySelector(
				'.b-nav-dropdown--open'
			);

			if ( !openedDropdown ) {

				return;

			}
			const dropdownButton = e.target.closest(
				'.b-nav-dropdown-column__title-wrapper'
			);

			if ( dropdownButton ) {

				dropdownButton.classList.toggle(
					'b-nav-dropdown-column__title-wrapper--open'
				);
				if (
					dropdownButton.classList.contains(
						'b-nav-dropdown-column__title-wrapper--open'
					)
				) {

					dropdownButton.setAttribute( 'aria-expanded', 'true' );

				} else {

					dropdownButton.setAttribute( 'aria-expanded', 'false' );

				}

				const dropdownContent = dropdownButton
					.closest( '.b-nav-dropdown-column' )
					.querySelector( '.b-nav-dropdown-column__inner' );

				if ( dropdownContent ) {

					dropdownContent.classList.toggle(
						'b-nav-dropdown-column__inner--open'
					);
					if (
						dropdownContent.classList.contains(
							'b-nav-dropdown-column__inner--open'
						)
					) {

						requestAnimationFrame( () => {

							dropdownContent.style.setProperty(
								'--dropdown-content-height',
								`${dropdownContent.scrollHeight}px`
							);

						} );

					} else {

						dropdownContent.style.setProperty(
							'--dropdown-content-height',
							`0px`
						);

					}

				}

			}

		} );

	};

}
